import React, { useState, useEffect } from 'react';
import Header from '../../components/navigation/Header';
import GlobalFilterBar from '../../components/navigation/GlobalFilterBar';
import ComplianceAlertBanner from '../../components/navigation/ComplianceAlertBanner';
import DataRefreshIndicator from '../../components/navigation/DataRefreshIndicator';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import supportTicketService from '../../services/supportTicketService';
import TicketStatisticsCard from './components/TicketStatisticsCard';
import TicketQueueTable from './components/TicketQueueTable';
import TicketDetailsModal from './components/TicketDetailsModal';
import EscalationManagementPanel from './components/EscalationManagementPanel';
import AnalyticsReportingPanel from './components/AnalyticsReportingPanel';

const SupportTicketManagementCenter = () => {
  const [tickets, setTickets] = useState([]);
  const [filteredTickets, setFilteredTickets] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [activeTab, setActiveTab] = useState('queue');
  const [filters, setFilters] = useState({
    status: 'all',
    priority: 'all',
    category: 'all',
    slaStatus: 'all',
    searchTerm: ''
  });

  const handleGlobalFilterChange = (globalFilters) => {
    // Handle global filter changes if needed
    console.log('Global filters changed:', globalFilters);
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [tickets, filters]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');

      const [ticketsData, statsData] = await Promise.all([
        supportTicketService?.getAllTickets(),
        supportTicketService?.getTicketStatistics()
      ]);

      setTickets(ticketsData);
      setStatistics(statsData);
    } catch (err) {
      setError(err?.message || 'Failed to load support tickets');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...tickets];

    // Search filter
    if (filters?.searchTerm) {
      const term = filters?.searchTerm?.toLowerCase();
      filtered = filtered?.filter(ticket =>
        ticket?.ticketNumber?.toLowerCase()?.includes(term) ||
        ticket?.subject?.toLowerCase()?.includes(term) ||
        ticket?.clientContactName?.toLowerCase()?.includes(term) ||
        ticket?.companyName?.toLowerCase()?.includes(term)
      );
    }

    // Status filter
    if (filters?.status !== 'all') {
      filtered = filtered?.filter(ticket => ticket?.ticketStatus === filters?.status);
    }

    // Priority filter
    if (filters?.priority !== 'all') {
      filtered = filtered?.filter(ticket => ticket?.priority === filters?.priority);
    }

    // Category filter
    if (filters?.category !== 'all') {
      filtered = filtered?.filter(ticket => ticket?.category === filters?.category);
    }

    // SLA status filter
    if (filters?.slaStatus !== 'all') {
      filtered = filtered?.filter(ticket => ticket?.slaStatus === filters?.slaStatus);
    }

    setFilteredTickets(filtered);
  };

  const handleViewDetails = (ticket) => {
    setSelectedTicket(ticket);
    setShowDetailsModal(true);
  };

  const handleCloseModal = () => {
    setShowDetailsModal(false);
    setSelectedTicket(null);
  };

  const handleStatusUpdate = async (ticketId, newStatus) => {
    try {
      await supportTicketService?.updateTicketStatus(ticketId, newStatus, 'admin@system.com');
      await loadData();
    } catch (err) {
      setError(err?.message || 'Failed to update ticket status');
    }
  };

  const handleExport = () => {
    try {
      const headers = [
        'Ticket Number',
        'Company',
        'Client Contact',
        'Subject',
        'Category',
        'Priority',
        'Status',
        'SLA Status',
        'Assigned Agent',
        'Created Date'
      ];

      const rows = filteredTickets?.map(ticket => [
        ticket?.ticketNumber || '',
        ticket?.companyName || '',
        ticket?.clientContactName || '',
        ticket?.subject || '',
        ticket?.category || '',
        ticket?.priority || '',
        ticket?.ticketStatus || '',
        ticket?.slaStatus || '',
        ticket?.assignedAgent || 'Unassigned',
        new Date(ticket?.createdAt)?.toLocaleDateString() || ''
      ]);

      const csvContent = [
        headers?.join(','),
        ...rows?.map(row => row?.map(cell => `"${cell}"`)?.join(','))
      ]?.join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL?.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `support-tickets-${new Date()?.toISOString()?.split('T')?.[0]}.csv`;
      link?.click();
      window.URL?.revokeObjectURL(url);
    } catch (err) {
      setError('Failed to export tickets');
    }
  };

  const tabs = [
    { id: 'queue', label: 'Ticket Queue', icon: 'Inbox' },
    { id: 'escalation', label: 'Escalation Management', icon: 'AlertTriangle' },
    { id: 'analytics', label: 'Analytics & Reporting', icon: 'BarChart3' }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <ComplianceAlertBanner />
      <GlobalFilterBar onFilterChange={handleGlobalFilterChange} />

      <main className="container mx-auto px-4 py-6">
        {/* Page Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold text-foreground">Support Ticket Management Center</h1>
              <p className="text-muted-foreground mt-1">
                Comprehensive help desk functionality for tracking client support requests and resolutions
              </p>
            </div>
            <div className="flex items-center gap-3">
              <DataRefreshIndicator onRefresh={loadData} />
              <Button onClick={handleExport} variant="outline">
                <Icon name="Download" size={16} className="mr-2" />
                Export
              </Button>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-2">
              <Icon name="AlertCircle" size={20} className="text-destructive" />
              <p className="text-sm text-destructive">{error}</p>
            </div>
          </div>
        )}

        {/* Statistics Cards */}
        {statistics && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
            <TicketStatisticsCard
              title="Active Tickets"
              value={statistics?.openTickets + statistics?.inProgressTickets}
              subtitle={`${statistics?.openTickets} open, ${statistics?.inProgressTickets} in progress`}
              icon="Inbox"
              color="blue"
            />
            <TicketStatisticsCard
              title="Critical Priority"
              value={statistics?.criticalTickets}
              subtitle="Requires immediate attention"
              icon="AlertTriangle"
              color="red"
            />
            <TicketStatisticsCard
              title="Avg Resolution Time"
              value={`${Math.round(statistics?.avgResolutionTime / 60)}h`}
              subtitle={`${statistics?.avgResolutionTime} minutes`}
              icon="Clock"
              color="amber"
            />
            <TicketStatisticsCard
              title="Client Satisfaction"
              value={statistics?.avgSatisfaction?.toFixed(1)}
              subtitle="Out of 5.0 rating"
              icon="Star"
              color="green"
            />
          </div>
        )}

        {/* Tabs */}
        <div className="border-b border-border mb-6">
          <div className="flex gap-6">
            {tabs?.map(tab => (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors ${
                  activeTab === tab?.id
                    ? 'border-primary text-primary font-medium' :'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon name={tab?.icon} size={18} />
                {tab?.label}
              </button>
            ))}
          </div>
        </div>

        {/* Filters */}
        {activeTab === 'queue' && (
          <div className="bg-card border border-border rounded-lg p-4 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Search</label>
                <input
                  type="text"
                  placeholder="Search tickets..."
                  value={filters?.searchTerm}
                  onChange={(e) => setFilters({ ...filters, searchTerm: e?.target?.value })}
                  className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Status</label>
                <select
                  value={filters?.status}
                  onChange={(e) => setFilters({ ...filters, status: e?.target?.value })}
                  className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground"
                >
                  <option value="all">All Status</option>
                  <option value="open">Open</option>
                  <option value="in_progress">In Progress</option>
                  <option value="pending_client">Pending Client</option>
                  <option value="resolved">Resolved</option>
                  <option value="closed">Closed</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Priority</label>
                <select
                  value={filters?.priority}
                  onChange={(e) => setFilters({ ...filters, priority: e?.target?.value })}
                  className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground"
                >
                  <option value="all">All Priorities</option>
                  <option value="critical">Critical</option>
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                  <option value="low">Low</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Category</label>
                <select
                  value={filters?.category}
                  onChange={(e) => setFilters({ ...filters, category: e?.target?.value })}
                  className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground"
                >
                  <option value="all">All Categories</option>
                  <option value="technical">Technical</option>
                  <option value="billing">Billing</option>
                  <option value="compliance">Compliance</option>
                  <option value="training">Training</option>
                  <option value="feature_request">Feature Request</option>
                  <option value="bug_report">Bug Report</option>
                  <option value="general">General</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">SLA Status</label>
                <select
                  value={filters?.slaStatus}
                  onChange={(e) => setFilters({ ...filters, slaStatus: e?.target?.value })}
                  className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground"
                >
                  <option value="all">All SLA Status</option>
                  <option value="within_sla">Within SLA</option>
                  <option value="approaching_breach">Approaching Breach</option>
                  <option value="breached">Breached</option>
                </select>
              </div>
            </div>
          </div>
        )}

        {/* Tab Content */}
        <div className="bg-card border border-border rounded-lg">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <Icon name="Loader2" size={32} className="animate-spin text-primary mx-auto mb-3" />
                <p className="text-muted-foreground">Loading support tickets...</p>
              </div>
            </div>
          ) : (
            <>
              {activeTab === 'queue' && (
                <TicketQueueTable
                  tickets={filteredTickets}
                  onViewDetails={handleViewDetails}
                  onStatusUpdate={handleStatusUpdate}
                />
              )}
              {activeTab === 'escalation' && (
                <EscalationManagementPanel tickets={tickets} onRefresh={loadData} />
              )}
              {activeTab === 'analytics' && (
                <AnalyticsReportingPanel tickets={tickets} statistics={statistics} />
              )}
            </>
          )}
        </div>
      </main>

      {/* Ticket Details Modal */}
      {showDetailsModal && selectedTicket && (
        <TicketDetailsModal
          ticket={selectedTicket}
          onClose={handleCloseModal}
          onRefresh={loadData}
        />
      )}
    </div>
  );
};

export default SupportTicketManagementCenter;